import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

function getHourInTimezone(tz) {
  try {
    const now = new Date();
    const hour = parseInt(
      new Intl.DateTimeFormat('en-US', { hour: 'numeric', hour12: false, timeZone: tz }).format(now),
      10
    );
    return hour;
  } catch {
    return new Date().getHours();
  }
}

function isNightHour(hour) {
  return hour >= 19 || hour < 7;
}

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    return localStorage.getItem('influencer_panel_theme_mode') || 'auto';
  });

  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('influencer_panel_theme');
    return saved !== 'light';
  });

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const resolveAutoTheme = useCallback(() => {
    const hour = getHourInTimezone(timezone);
    return !isNightHour(hour);
  }, [timezone]);

  useEffect(() => {
    localStorage.setItem('influencer_panel_theme_mode', mode);
  }, [mode]);

  useEffect(() => {
    if (mode === 'auto') {
      const light = resolveAutoTheme();
      setIsDark(!light);
      localStorage.setItem('influencer_panel_theme', light ? 'light' : 'dark');

      const interval = setInterval(() => {
        const light = resolveAutoTheme();
        setIsDark(!light);
        localStorage.setItem('influencer_panel_theme', light ? 'light' : 'dark');
      }, 60_000);

      return () => clearInterval(interval);
    }
  }, [mode, resolveAutoTheme]);

  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('panel-dark');
      root.classList.remove('panel-light');
    } else {
      root.classList.add('panel-light');
      root.classList.remove('panel-dark');
    }
  }, [isDark]);

  const toggleTheme = () => {
    setMode((prev) => {
      if (prev === 'auto') {
        const light = resolveAutoTheme();
        const next = light ? 'dark' : 'light';
        setIsDark(next === 'dark');
        localStorage.setItem('influencer_panel_theme', next);
        return next;
      }
      if (prev === 'dark') {
        setIsDark(false);
        localStorage.setItem('influencer_panel_theme', 'light');
        return 'light';
      }
      {
        const light = resolveAutoTheme();
        setIsDark(!light);
        localStorage.setItem('influencer_panel_theme', !light ? 'dark' : 'light');
        return 'auto';
      }
    });
  };

  return (
    <ThemeContext.Provider value={{ isDark, mode, toggleTheme, timezone }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
